/**
 * Analytics Dashboard Page
 * Displays KPIs and analytics for the organization
 */

import React, { useMemo } from "react";
import Icon from "../../components/AppIcon";
import Loader from "../../components/ui/Loader";
import { useGetDashboardQuery } from "../../store/api/analyticsApi";
import { useListPaymentsQuery } from "../../store/api/paymentsApi";
import { formatCurrency } from "../../utils/formatCurrency";
import PaymentStatusChart from "./components/PaymentStatusChart";
import PropertyStatusChart from "./components/PropertyStatusChart";
import RevenueChart from "./components/RevenueChart";

const AnalyticsDashboard: React.FC = () => {
	const { data: dashboardData, isLoading, error } = useGetDashboardQuery();
	// Fetch all payments for historical revenue data
	const { data: paymentsData } = useListPaymentsQuery({
		page_size: 1000, // Get all payments for chart
		ordering: "-paid_date",
	});

	// Generate real revenue chart data from actual payments
	// Must be called before early returns to follow Rules of Hooks
	const revenueData = useMemo(() => {
		const months = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];
		const currentMonth = new Date().getMonth();
		const currentYear = new Date().getFullYear();
		const monthsToShow = months.slice(
			Math.max(0, currentMonth - 5),
			currentMonth + 1,
		);

		// Group payments by month
		const monthlyData: Record<string, { revenue: number; pending: number }> =
			{};

		// Initialize all months with zero
		monthsToShow.forEach((month, index) => {
			const monthIndex = currentMonth - 5 + index;
			if (monthIndex >= 0) {
				monthlyData[month] = { revenue: 0, pending: 0 };
			}
		});

		// Process actual payments
		// Using correct enum values: paid, pending, overdue (matching backend PaymentStatus)
		if (paymentsData?.results) {
			paymentsData.results.forEach((payment) => {
				// Revenue: only count paid payments
				if (payment.paid_date && payment.status === "paid") {
					const paidDate = new Date(payment.paid_date);
					if (
						paidDate.getFullYear() === currentYear &&
						paidDate.getMonth() >= currentMonth - 5 &&
						paidDate.getMonth() <= currentMonth
					) {
						const monthName = months[paidDate.getMonth()];
						if (monthlyData[monthName]) {
							monthlyData[monthName].revenue += Number(payment.amount || 0);
						}
					}
				}

				// Pending: count pending and overdue payments by due date
				if (payment.status === "pending" || payment.status === "overdue") {
					if (payment.due_date) {
						const dueDate = new Date(payment.due_date);
						if (
							dueDate.getFullYear() === currentYear &&
							dueDate.getMonth() >= currentMonth - 5 &&
							dueDate.getMonth() <= currentMonth
						) {
							const monthName = months[dueDate.getMonth()];
							if (monthlyData[monthName]) {
								monthlyData[monthName].pending += Number(payment.amount || 0);
							}
						}
					}
				}
			});
		}

		return monthsToShow.map((month) => ({
			month,
			revenue: monthlyData[month]?.revenue || 0,
			pending: monthlyData[month]?.pending || 0,
		}));
	}, [paymentsData]);

	const propertyStatusData = useMemo(() => {
		if (!dashboardData) {
			return [
				{ name: "Available", value: 0, color: "hsl(var(--success))" },
				{ name: "Occupied", value: 0, color: "hsl(var(--primary))" },
				{ name: "Pending", value: 0, color: "hsl(var(--accent))" },
				{ name: "Maintenance", value: 0, color: "hsl(var(--warning))" },
			];
		}
		return [
			{
				name: "Available",
				value: dashboardData.properties.available,
				color: "hsl(var(--success))",
			},
			{
				name: "Occupied",
				value: dashboardData.properties.occupied,
				color: "hsl(var(--primary))",
			},
			{
				name: "Pending",
				value: dashboardData.properties.pending || 0,
				color: "hsl(var(--accent))",
			},
			{
				name: "Maintenance",
				value: dashboardData.properties.maintenance || 0,
				color: "hsl(var(--warning))",
			},
		].filter((item) => item.value > 0); // Only show statuses with properties
	}, [dashboardData]);

	// Calculate real payment status data from actual payments
	// Using correct enum values: paid, pending, overdue, cancelled, refunded
	const paymentStatusData = useMemo(() => {
		if (!paymentsData?.results) {
			return [
				{
					status: "Paid",
					count: 0,
					amount: 0,
				},
				{
					status: "Pending",
					count: 0,
					amount: 0,
				},
				{
					status: "Overdue",
					count: 0,
					amount: 0,
				},
			];
		}

		// Filter by correct enum values (matching backend PaymentStatus enum)
		const paidPayments = paymentsData.results.filter(
			(p) => p.status === "paid",
		);
		const pendingPayments = paymentsData.results.filter(
			(p) => p.status === "pending",
		);
		const overduePayments = paymentsData.results.filter(
			(p) => p.status === "overdue",
		);
		// Note: cancelled and refunded are valid statuses but not shown in main chart
		// They can be added if needed for comprehensive reporting

		return [
			{
				status: "Paid",
				count: paidPayments.length,
				amount: paidPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0),
			},
			{
				status: "Pending",
				count: pendingPayments.length,
				amount: pendingPayments.reduce(
					(sum, p) => sum + Number(p.amount || 0),
					0,
				),
			},
			{
				status: "Overdue",
				count: overduePayments.length,
				amount: overduePayments.reduce(
					(sum, p) => sum + Number(p.amount || 0),
					0,
				),
			},
		];
	}, [paymentsData]);

	// Early returns AFTER all hooks (Rules of Hooks compliance)
	if (isLoading) {
		return <Loader size="md" className="min-h-[400px]" />;
	}

	if (error || !dashboardData) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Dashboard
				</h2>
				<p className="text-muted-foreground">
					Failed to load dashboard data. Please try again.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Header */}
			<div className="mb-4 sm:mb-6">
				<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
					Analytics Dashboard
				</h1>
				<p className="text-muted-foreground mt-1">
					Overview of your property management metrics
				</p>
			</div>

			{/* KPI Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
				{/* Properties Card */}
				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-4">
						<div className="p-3 bg-primary/10 rounded-lg">
							<Icon name="Home" size={24} className="text-primary" />
						</div>
						<span className="text-sm text-muted-foreground">Properties</span>
					</div>
					<div className="space-y-2">
						<p className="text-3xl font-bold text-foreground">
							{dashboardData.properties.total}
						</p>
						<div className="flex items-center gap-4 text-sm">
							<span className="text-muted-foreground">
								{dashboardData.properties.available} available
							</span>
							<span className="text-muted-foreground">
								{dashboardData.properties.occupied} occupied
							</span>
						</div>
						<div className="pt-2 border-t border-border">
							<div className="flex items-center justify-between text-sm">
								<span className="text-muted-foreground">Occupancy Rate</span>
								<span className="font-semibold text-foreground">
									{dashboardData.properties.occupancy_rate}%
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Revenue Card */}
				<div className="bg-card border border-border rounded-lg p-4 sm:p-6 card-shadow overflow-hidden">
					<div className="flex items-center justify-between mb-4 gap-2">
						<div className="p-2 sm:p-3 bg-success/10 rounded-lg flex-shrink-0">
							<Icon
								name="Currency"
								size={20}
								className="text-success sm:w-6 sm:h-6"
							/>
						</div>
						<span className="text-xs sm:text-sm text-muted-foreground truncate">
							Revenue
						</span>
					</div>
					<div className="space-y-2 min-w-0">
						<p className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate">
							{formatCurrency(dashboardData.payments.total_revenue, {
								showAbbreviation: true,
							})}
						</p>
						<div className="flex items-center gap-4 text-xs sm:text-sm">
							<span className="text-muted-foreground truncate">
								This month:{" "}
								{formatCurrency(dashboardData.payments.monthly_revenue, {
									showAbbreviation: true,
								})}
							</span>
						</div>
						<div className="pt-2 border-t border-border">
							<div className="flex items-center justify-between text-xs sm:text-sm gap-2">
								<span className="text-muted-foreground truncate">
									Total Payments
								</span>
								<span className="font-semibold text-foreground flex-shrink-0">
									{dashboardData.payments.total}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Payments Status Card */}
				<div className="bg-card border border-border rounded-lg p-4 sm:p-6 card-shadow overflow-hidden">
					<div className="flex items-center justify-between mb-4 gap-2">
						<div className="p-2 sm:p-3 bg-warning/10 rounded-lg flex-shrink-0">
							<Icon
								name="CreditCard"
								size={20}
								className="text-warning sm:w-6 sm:h-6"
							/>
						</div>
						<span className="text-xs sm:text-sm text-muted-foreground truncate">
							Payments
						</span>
					</div>
					<div className="space-y-2 min-w-0">
						<p className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground truncate">
							{dashboardData.payments.pending}
						</p>
						<div className="flex items-center gap-4 text-xs sm:text-sm">
							<span className="text-muted-foreground truncate">Pending</span>
						</div>
						<div className="pt-2 border-t border-border">
							<div className="flex items-center justify-between text-xs sm:text-sm gap-2">
								<span className="text-muted-foreground truncate">Overdue</span>
								<span className="font-semibold text-destructive flex-shrink-0">
									{dashboardData.payments.overdue}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Maintenance Card */}
				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-4">
						<div className="p-3 bg-accent/10 rounded-lg">
							<Icon name="Wrench" size={24} className="text-accent" />
						</div>
						<span className="text-sm text-muted-foreground">Maintenance</span>
					</div>
					<div className="space-y-2">
						<p className="text-3xl font-bold text-foreground">
							{dashboardData.maintenance.total}
						</p>
						<div className="flex items-center gap-4 text-sm">
							<span className="text-muted-foreground">
								{dashboardData.maintenance.pending} pending
							</span>
							<span className="text-muted-foreground">
								{dashboardData.maintenance.in_progress} in progress
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Charts Section */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
				<RevenueChart data={revenueData} />
				<PropertyStatusChart data={propertyStatusData} />
			</div>

			<PaymentStatusChart data={paymentStatusData} />

			{/* Users Summary */}
			<div className="bg-card border border-border rounded-lg p-4 sm:p-6 card-shadow">
				<h2 className="text-xl font-semibold text-foreground mb-4">Users</h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
					<div>
						<p className="text-sm text-muted-foreground mb-1">Total Users</p>
						<p className="text-2xl font-bold text-foreground">
							{dashboardData.users.total}
						</p>
					</div>
					<div>
						<p className="text-sm text-muted-foreground mb-1">Tenants</p>
						<p className="text-2xl font-bold text-foreground">
							{dashboardData.users.tenants}
						</p>
					</div>
					<div>
						<p className="text-sm text-muted-foreground mb-1">Technicians</p>
						<p className="text-2xl font-bold text-foreground">
							{dashboardData.users.technicians}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AnalyticsDashboard;
